import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import i18n from "i18n";
import { isHealthFeature } from "./HealthReport.types";
import { countLeavesByStatus, countProblems, filterProblems, formatLeafCount } from "./healthTree";
import useHealthReport from "./useHealthReport";
export const SYNTHETIC_ROOT_ID = "cluster_health";
const HEALTH_NAMESPACE = "enterprise_health";
const toTreeData = (node) => ({
  value: node.id,
  label: node.title,
  nodeProps: {
    status: node.status,
    countSummary: isHealthFeature(node) ? void 0 : formatLeafCount(node)
  },
  children: isHealthFeature(node) ? node.children.map(toTreeData) : void 0
});
const buildLookup = (root) => {
  const lookup = /* @__PURE__ */ Object.create(null);
  const visit = (node) => {
    lookup[node.id] = node;
    if (isHealthFeature(node)) {
      node.children.forEach(visit);
    }
  };
  visit(root);
  return lookup;
};
const buildPaths = (root) => {
  const paths = /* @__PURE__ */ Object.create(null);
  const visit = (node, parentPath = []) => {
    const path = [...parentPath, { id: node.id, title: node.title }];
    paths[node.id] = path;
    if (isHealthFeature(node)) {
      node.children.forEach((child) => visit(child, path));
    }
  };
  visit(root);
  return paths;
};
const displayTitle = (node) => {
  var _a;
  const key = `titles.${(_a = node.id.split(".").pop()) != null ? _a : node.id}`;
  return i18n.exists(key, { ns: HEALTH_NAMESPACE }) ? i18n.t(key, { ns: HEALTH_NAMESPACE }) : node.title;
};
const withDisplayTitle = (node) => {
  const title = displayTitle(node);
  return isHealthFeature(node) ? { ...node, title, children: node.children.map(withDisplayTitle) } : { ...node, title };
};
const buildHealthModuleState = (report) => {
  const root = {
    id: SYNTHETIC_ROOT_ID,
    title: i18n.t("root_title", { ns: HEALTH_NAMESPACE }),
    status: report.overall_status,
    children: report.features.map(withDisplayTitle)
  };
  return {
    root,
    treeData: [toTreeData(root)],
    lookup: buildLookup(root),
    paths: buildPaths(root),
    initialExpandedState: { [root.id]: true }
  };
};
const EMPTY_REPORT = { overall_status: "unknown", generated_at: "", features: [] };
const useHealthModule = ({ problemsOnly = false } = {}) => {
  const { data, error, isFetching, isLoading } = useHealthReport();
  const {
    i18n: { language }
  } = useTranslation(HEALTH_NAMESPACE);
  const reportState = useMemo(() => {
    const report = data != null ? data : EMPTY_REPORT;
    return buildHealthModuleState(problemsOnly ? filterProblems(report) : report);
  }, [data, problemsOnly, language]);
  return {
    ...reportState,
    generatedAt: data == null ? void 0 : data.generated_at,
    hasReport: !!data,
    isLoading: Boolean(isLoading && !data),
    isFetching: Boolean(isFetching),
    error: error != null ? error : null
  };
};
export const useHealthSummary = () => {
  var _a;
  const { data, error, isLoading } = useHealthReport();
  const statusCounts = useMemo(() => {
    var _a2;
    return countLeavesByStatus((_a2 = data == null ? void 0 : data.features) != null ? _a2 : []);
  }, [data]);
  return {
    overallStatus: (_a = data == null ? void 0 : data.overall_status) != null ? _a : "unknown",
    statusCounts,
    problemCount: countProblems(statusCounts),
    totalCheckCount: Object.values(statusCounts).reduce((total, count) => total + count, 0),
    hasReport: !!data,
    error: error != null ? error : null,
    isLoading: Boolean(isLoading && !data),
    generatedAt: data == null ? void 0 : data.generated_at
  };
};
export default useHealthModule;
